import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { completeBusinessSurveyAndNavigate } from "../../util/organizationNavigation";
import { useOrganizationVendors, usePrimaryOrganization, useSelf } from "../../hooks";
import { BUSINESS_SURVEY_NAME, businessSurvey } from "../../surveys/businessSurvey";
import { SurveyAnswerFn, SurveyAnswers, useSurvey } from "../../../common/hooks/api/useSurvey";
import { hasAnswered, questionsWithVisibility, QuestionWithVisibility } from "../../surveys/survey";
import { SurveyQuestion } from "../../components/get-compliant/survey/SurveyQuestion";
import { PageLoading } from "../../components";
import { Button, Modal, OnboardingOrganizationDropdown, useSnackBar } from "@truevault/aquilo";
import clsx from "clsx";
import { useUpdateDataSources } from "../get-compliant/data-map/ConsumerDataSourcesPage";
import { Routes } from "../../root/routes";
import { HR_SURVEY_NAME, hrSurvey } from "../../surveys/hrSurvey";
import { ArrowLongRightIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, CheckIcon } from "@heroicons/react/24/solid";
import { FlagForReviewButton } from "../../components/surveys/FlagForReviewButton";
import {
  EEA_UK_SURVEY_NAME,
  eeaUkSurvey,
  HAS_DATA_PRIVACY_OFFICER_SLUG,
  HAS_DESIGNATED_REP_SLUG,
  hasDesignatedRepresentative,
  isDataPrivacyOfficerRequired,
} from "../../surveys/eeaUkSurvey";
import {
  CONSUMER_INCENTIVES_SURVEY_NAME,
  consumerCollectionIncentivesSurvey,
  OFFERS_CONSUMER_INCENTIVES_SLUG,
} from "../../surveys/consumerCollectionIncentivesSurvey";
import {
  PrivacyCenterSettingsProps,
  usePrivacyCenterSettings,
} from "../../components/superorg/settings/PrivacyCenterSettings";
import { DpoValues, EditDpoDialog } from "../../components/dialog/EditDpoDialog";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { PrivacyCenterPipedaPrivacyOfficerRow } from "../../components/organisms/settings/rows/PrivacyCenterPipedaPrivacyOfficerRow";
import { filterVendorsForDataSourceSurveyQuestion } from "../../components/get-compliant/survey/custom/AddDataSourceQuestion";
import { EditEeaContactDialog, EeaContactValues } from "../../components/dialog/EEAContactDialog";
import { Api } from "../../../common/service/Api";
import { SurveyBooleanQuestionProps } from "../../components/get-compliant/survey/SurveyBooleanQuestion";
import { PrivacyCenterDto } from "../../../common/service/server/dto/PrivacyCenterDto";
import { OmniBar } from "../../components/dialog/OmniBar";
import { OmniBarProvider } from "../../../common/hooks/useOmniBar";
import { Link } from "react-router-dom";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../get-compliant/data-recipients/shared";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { useDataMap } from "../../../common/hooks/api/useDataMap";
import { DataMapDto } from "../../../common/service/server/dto/datamap/DataMapDto";
import { useProductAnalytics } from "../../analytics/productAnalytics";

type CurrentView =
  | "business-survey-welcome"
  | "business-survey"
  // Skip welcome screen for incentives survey since its just currently one question
  | "consumer-collection-incentives-survey"
  | "eea-uk-survey-welcome"
  | "eea-uk-survey"
  | "pipeda-privacy-officer"
  | "hr-survey"
  | "finished-review-modal";

export function BusinessSurveyAquiloPage() {
  const showSnackbar = useSnackBar();
  const captureProductAnalyticsEvent = useProductAnalytics({
    flow: "business_data_profile",
  });
  const flowStartedRef = useRef(false);

  const [org] = usePrimaryOrganization();
  const [self] = useSelf();

  const [currentView, setCurrentView] = useState<CurrentView>(null);

  const { settings: pcSettings, privacyCenter } = usePrivacyCenterSettings();

  const {
    answers: bizAnswers,
    setAnswer: setBizAnswer,
    remote: bizSurveyRemote,
    flaggedForReviewSlugs: bizFlaggedForReviewSlugs,
  } = useSurvey(org?.id, BUSINESS_SURVEY_NAME);

  const { answers: sellingAndSharingAnswers, request: sellingAndSharingRequest } = useSurvey(
    org?.id,
    SELLING_AND_SHARING_SURVEY_NAME,
  );
  const [dataRecipients, dataRecipientsRequest] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ ["CONSUMER"],
    /* onlyUseSettled = */ true,
  );
  const sellingAndSharingAnswersForBusinessSurvey = sellingAndSharingRequest.success
    ? sellingAndSharingAnswers
    : undefined;
  const dataRecipientsForBusinessSurvey = dataRecipientsRequest.success
    ? dataRecipients
    : undefined;
  // Used to list the org's selling vendors and their sensitive PICs in the New Jersey question help text
  const { data: dataMap } = useDataMap(org?.id);

  const {
    answers: incentivesAnswers,
    setAnswer: setIncentivesAnswer,
    remote: incentivesSurveyRemote,
    flaggedForReviewSlugs: incentivesFlaggedForReviewSlugs,
  } = useSurvey(org?.id, CONSUMER_INCENTIVES_SURVEY_NAME);

  const updateDataSources = useUpdateDataSources(org?.id);

  const {
    answers: hrAnswers,
    setAnswer: setHrAnswer,
    remote: hrRemote,
    flaggedForReviewSlugs: hrFlaggedForReviewSlugs,
  } = useSurvey(org?.id, HR_SURVEY_NAME);

  const {
    answers: eeaAnswers,
    setAnswer: setEeaAnswer,
    remote: eeaRemote,
    flaggedForReviewSlugs: eeaFlaggedForReviewSlugs,
  } = useSurvey(org?.id, EEA_UK_SURVEY_NAME);

  const remoteBizSurveyAnswers = useMemo<SurveyAnswers>(
    () =>
      Object.fromEntries(
        Object.values(bizSurveyRemote?.answers ?? {}).map((q) => [q.slug, q.answer]),
      ),
    [bizSurveyRemote],
  );

  const remoteIncentivesAnswers = useMemo<SurveyAnswers>(
    () =>
      Object.fromEntries(
        Object.values(incentivesSurveyRemote?.answers ?? {}).map((q) => [q.slug, q.answer]),
      ),
    [incentivesSurveyRemote],
  );

  const remoteHrAnswers = useMemo<SurveyAnswers>(
    () => Object.fromEntries(Object.values(hrRemote?.answers ?? {}).map((q) => [q.slug, q.answer])),
    [hrRemote],
  );

  const remoteEeaAnswers = useMemo<SurveyAnswers>(
    () =>
      Object.fromEntries(Object.values(eeaRemote?.answers ?? {}).map((q) => [q.slug, q.answer])),
    [eeaRemote],
  );

  // DPO
  const isDpoRequired = org?.featureGdpr && isDataPrivacyOfficerRequired(eeaAnswers, privacyCenter);
  const hasDpo = Boolean(pcSettings?.dpoName) && Boolean(pcSettings?.dpoEmail);
  const [isEditDpoDialogOpen, setIsEditDpoDialogOpen] = useState(false);

  // Designated Representative
  const hasDesignatedRep = hasDesignatedRepresentative(pcSettings);
  const [isDesignatedRepDialogOpen, setIsDesignatedRepDialogOpen] = useState(false);

  const [currentQuestionSlug, setCurrentQuestionSlug] = useState<string | null>(null);

  const allFlaggedForReviewSlugs = useMemo(
    () => [
      ...bizFlaggedForReviewSlugs,
      ...incentivesFlaggedForReviewSlugs,
      ...hrFlaggedForReviewSlugs,
      ...eeaFlaggedForReviewSlugs,
    ],
    [
      bizFlaggedForReviewSlugs,
      incentivesFlaggedForReviewSlugs,
      hrFlaggedForReviewSlugs,
      eeaFlaggedForReviewSlugs,
    ],
  );

  useEffect(() => {
    if (
      currentView === null &&
      org &&
      privacyCenter &&
      bizAnswers &&
      Object.keys(bizAnswers).length > 0 &&
      incentivesAnswers &&
      Object.keys(incentivesAnswers).length > 0 &&
      (!org?.featureGdpr || (eeaAnswers && Object.keys(eeaAnswers).length > 0)) &&
      hrAnswers &&
      Object.keys(hrAnswers).length > 0 &&
      sellingAndSharingRequest.success &&
      dataRecipientsRequest.success &&
      updateDataSources &&
      pcSettings
    ) {
      let [initialView, initialQuestionSlug] = initialViewAndQuestionSlug(
        org,
        {
          bizAnswers,
          sellingAndSharingAnswers: sellingAndSharingAnswersForBusinessSurvey,
          dataRecipients: dataRecipientsForBusinessSurvey,
          hrAnswers,
          eeaAnswers,
          incentivesAnswers,
        },
        privacyCenter,
        pcSettings,
        allFlaggedForReviewSlugs,
      );
      setCurrentView(initialView);
      setCurrentQuestionSlug(initialQuestionSlug);
    }
  }, [
    org,
    bizAnswers,
    sellingAndSharingAnswersForBusinessSurvey,
    dataRecipientsForBusinessSurvey,
    incentivesAnswers,
    incentivesSurveyRemote,
    eeaAnswers,
    hrAnswers,
    sellingAndSharingRequest.success,
    dataRecipientsRequest.success,
    allFlaggedForReviewSlugs,
    pcSettings,
    updateDataSources,
    isDpoRequired,
    hasDpo,
    currentView,
  ]);

  let {
    bizQuestionsWithVisibility,
    incentivesQuestionsWithVisibility,
    hrQuestionsWithVisibility,
    eeaQuestionsWithVisibility,
  } = allSurveyQuestionsWithVisibility(org, privacyCenter, pcSettings, {
    bizAnswers,
    sellingAndSharingAnswers: sellingAndSharingAnswersForBusinessSurvey,
    dataRecipients: dataRecipientsForBusinessSurvey,
    dataMap,
    hrAnswers,
    eeaAnswers,
    incentivesAnswers,
  });

  let allQuestionsWithVisibility = [
    ...bizQuestionsWithVisibility,
    ...incentivesQuestionsWithVisibility,
    ...hrQuestionsWithVisibility,
    ...eeaQuestionsWithVisibility,
  ];

  let surveyProgress = (() => {
    let countAnswered = (questions: QuestionWithVisibility[], answers: SurveyAnswers) =>
      questions.filter(
        (q) =>
          q.visible &&
          (hasAnswered(answers, q, /* debug = */ false) ||
            allFlaggedForReviewSlugs.includes(q.slug)),
      ).length;
    let countVisible = (questions: QuestionWithVisibility[]) =>
      questions.filter((q) => q.visible).length;
    let ratio = (answered: number, total: number) => (total === 0 ? 0 : answered / total);

    let bizAnswered =
      countAnswered(bizQuestionsWithVisibility, bizAnswers) +
      countAnswered(incentivesQuestionsWithVisibility, incentivesAnswers);
    let bizTotal =
      countVisible(bizQuestionsWithVisibility) + countVisible(incentivesQuestionsWithVisibility);

    return {
      biz: ratio(bizAnswered, bizTotal),
      eea: ratio(
        countAnswered(eeaQuestionsWithVisibility, eeaAnswers),
        countVisible(eeaQuestionsWithVisibility),
      ),
      hr: ratio(
        countAnswered(hrQuestionsWithVisibility, hrAnswers),
        countVisible(hrQuestionsWithVisibility),
      ),
    };
  })();

  const surveyDataByView: Partial<
    Record<
      CurrentView,
      {
        currentAnswers: SurveyAnswers;
        currentRemoteAnswers: SurveyAnswers;
        currentSetAnswer: SurveyAnswerFn;
        currentQuestionsVisibilityWithDefault: QuestionWithVisibility[];
      }
    >
  > = {
    "business-survey": {
      currentAnswers: bizAnswers,
      currentRemoteAnswers: remoteBizSurveyAnswers,
      currentSetAnswer: setBizAnswer,
      currentQuestionsVisibilityWithDefault: bizQuestionsWithVisibility,
    },
    [CONSUMER_INCENTIVES_SURVEY_NAME]: {
      currentAnswers: incentivesAnswers,
      currentRemoteAnswers: remoteIncentivesAnswers,
      currentSetAnswer: setIncentivesAnswer,
      currentQuestionsVisibilityWithDefault: incentivesQuestionsWithVisibility,
    },
    "hr-survey": {
      currentAnswers: hrAnswers,
      currentRemoteAnswers: remoteHrAnswers,
      currentSetAnswer: setHrAnswer,
      currentQuestionsVisibilityWithDefault: hrQuestionsWithVisibility,
    },
    "eea-uk-survey": {
      currentAnswers: eeaAnswers,
      currentRemoteAnswers: remoteEeaAnswers,
      currentSetAnswer: setEeaAnswer,
      currentQuestionsVisibilityWithDefault: eeaQuestionsWithVisibility,
    },
  };

  let {
    currentQuestionsVisibilityWithDefault,
    currentAnswers,
    currentRemoteAnswers,
    currentSetAnswer,
  } = surveyDataByView[currentView] || {};
  let visibleCurrentQuestions = currentQuestionsVisibilityWithDefault?.filter((q) => q.visible);

  const isAnalyticsQuestionStep =
    Boolean(currentView) && isSurveyView(currentView) && Boolean(currentQuestionSlug);
  const analyticsQuestionPosition = isAnalyticsQuestionStep
    ? visibleCurrentQuestions?.findIndex((question) => question.slug === currentQuestionSlug) ?? -1
    : -1;
  const analyticsVisibleStepCount = visibleCurrentQuestions?.length ?? 0;
  const currentAnalyticsStep = useMemo(
    () =>
      currentView
        ? {
            step_id:
              isAnalyticsQuestionStep && currentQuestionSlug
                ? currentQuestionSlug
                : analyticsStepIdForView(currentView),
            section: currentView,
            ...(analyticsQuestionPosition >= 0
              ? {
                  step_position: analyticsQuestionPosition + 1,
                  visible_step_count: analyticsVisibleStepCount,
                }
              : {}),
          }
        : null,
    [
      analyticsQuestionPosition,
      analyticsVisibleStepCount,
      currentQuestionSlug,
      currentView,
      isAnalyticsQuestionStep,
    ],
  );

  useEffect(() => {
    if (!currentAnalyticsStep) {
      return;
    }

    if (!flowStartedRef.current) {
      flowStartedRef.current = true;
      captureProductAnalyticsEvent("onboarding_flow_started", {
        entry_step_id: currentAnalyticsStep.step_id,
        is_resumed: currentView !== "business-survey-welcome",
      });
    }

    captureProductAnalyticsEvent("onboarding_step_viewed", currentAnalyticsStep);
  }, [captureProductAnalyticsEvent, currentAnalyticsStep, currentView]);

  let currentQuestion = allQuestionsWithVisibility?.find((q) => q.slug === currentQuestionSlug);
  let currentQuestionText = currentQuestion?.plaintextQuestion;
  if (!currentQuestionText && typeof currentQuestion?.question == "string") {
    currentQuestionText = currentQuestion?.question;
  }

  const moveToPrevQuestion = useCallback(() => {
    if (currentView === "eea-uk-survey-welcome") {
      setCurrentView("consumer-collection-incentives-survey");
      let visibleIncentiveQuestions = incentivesQuestionsWithVisibility.filter((q) => q.visible);
      setCurrentQuestionSlug(visibleIncentiveQuestions[visibleIncentiveQuestions.length - 1].slug);
      return;
    } else if (currentView === "pipeda-privacy-officer") {
      if (org?.featureGdpr) {
        setCurrentView("eea-uk-survey");
        let visibleEeaQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);
        setCurrentQuestionSlug(visibleEeaQuestions[visibleEeaQuestions.length - 1].slug);
      } else {
        setCurrentView("consumer-collection-incentives-survey");
        let visibleIncentiveQuestions = incentivesQuestionsWithVisibility.filter((q) => q.visible);
        setCurrentQuestionSlug(
          visibleIncentiveQuestions[visibleIncentiveQuestions.length - 1].slug,
        );
      }
      return;
    }

    let idx = visibleCurrentQuestions.findIndex((q) => q.slug === currentQuestionSlug);
    let prevVisibleIdx = idx - 1;
    while (prevVisibleIdx >= 0 && !visibleCurrentQuestions[prevVisibleIdx].visible) {
      prevVisibleIdx--;
    }

    if (prevVisibleIdx >= 0) {
      setCurrentQuestionSlug(visibleCurrentQuestions[prevVisibleIdx].slug);
    } else {
      if (currentView === "business-survey") {
        setCurrentView("business-survey-welcome");
      } else if (currentView === "consumer-collection-incentives-survey") {
        setCurrentView("business-survey");
        let visibleBizQuestions = bizQuestionsWithVisibility.filter((q) => q.visible);
        setCurrentQuestionSlug(visibleBizQuestions[visibleBizQuestions.length - 1].slug);
      } else if (currentView === "eea-uk-survey") {
        setCurrentView("eea-uk-survey-welcome");
      } else if (currentView === "hr-survey") {
        if (org.featurePipeda) {
          setCurrentView("pipeda-privacy-officer");
        } else if (org.featureGdpr) {
          setCurrentView("eea-uk-survey");
          let visibleEeaQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);
          setCurrentQuestionSlug(visibleEeaQuestions[visibleEeaQuestions.length - 1].slug);
        } else {
          setCurrentView("consumer-collection-incentives-survey");
          let visibleIncentiveQuestions = incentivesQuestionsWithVisibility.filter(
            (q) => q.visible,
          );
          setCurrentQuestionSlug(
            visibleIncentiveQuestions[visibleIncentiveQuestions.length - 1].slug,
          );
        }
      }
    }
  }, [
    bizQuestionsWithVisibility,
    currentQuestionSlug,
    currentView,
    eeaQuestionsWithVisibility,
    isDpoRequired,
    org,
    visibleCurrentQuestions,
  ]);

  const moveToNextQuestion = useCallback(
    (updatedAnswers: SurveyAnswers, completionSource = "next_button") => {
      if (
        currentAnalyticsStep &&
        (!currentQuestionSlug || !allFlaggedForReviewSlugs.includes(currentQuestionSlug))
      ) {
        captureProductAnalyticsEvent("onboarding_step_completed", {
          ...currentAnalyticsStep,
          completion_source: completionSource,
        });
      }

      if (currentView === "eea-uk-survey-welcome") {
        setCurrentView("eea-uk-survey");
        let visibleEeaQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);
        setCurrentQuestionSlug(visibleEeaQuestions[0].slug);
        return;
      }

      // Re-calculate the survey answers with the latest `updatedAnswers`
      let allUpdatedAnswers = {
        bizAnswers,
        sellingAndSharingAnswers: sellingAndSharingAnswersForBusinessSurvey,
        dataRecipients: dataRecipientsForBusinessSurvey,
        dataMap,
        incentivesAnswers,
        eeaAnswers,
        hrAnswers,
      };
      if (currentView === "business-survey") {
        allUpdatedAnswers.bizAnswers = updatedAnswers;
      } else if (currentView === "consumer-collection-incentives-survey") {
        allUpdatedAnswers.incentivesAnswers = updatedAnswers;
      } else if (currentView === "eea-uk-survey") {
        allUpdatedAnswers.eeaAnswers = updatedAnswers;
      } else if (currentView === "hr-survey") {
        allUpdatedAnswers.hrAnswers = updatedAnswers;
      }

      // Re-calculate survey questions visibliity with new survey answers
      let {
        bizQuestionsWithVisibility: newBizQuestionsWithVisibility,
        incentivesQuestionsWithVisibility: newIncentivesQuestionsWithVisibility,
        hrQuestionsWithVisibility: newHrQuestionsWithVisibility,
        eeaQuestionsWithVisibility: newEeaQuestionsWithVisibility,
      } = allSurveyQuestionsWithVisibility(org, privacyCenter, pcSettings, allUpdatedAnswers);

      let visibleBizQuestions = newBizQuestionsWithVisibility.filter((q) => q.visible);
      let visibleIncentivesQuestions = newIncentivesQuestionsWithVisibility.filter(
        (q) => q.visible,
      );
      let visibleHrQuestions = newHrQuestionsWithVisibility.filter((q) => q.visible);
      let visibleEeaQuestions = newEeaQuestionsWithVisibility.filter((q) => q.visible);

      // Get the current set of visible questions
      let currentVisibleQuestions = {
        "business-survey": visibleBizQuestions,
        "consumer-collection-incentives-survey": visibleIncentivesQuestions,
        "hr-survey": visibleHrQuestions,
        "eea-uk-survey": visibleEeaQuestions,
      }[currentView];

      if (currentView === "pipeda-privacy-officer") {
        setCurrentView("hr-survey");
        setCurrentQuestionSlug(visibleHrQuestions[0].slug);
        return;
      }

      // Find the index of the next question within the current set of visible questions, or move to the next view
      let idx = currentVisibleQuestions.findIndex((q) => q.slug === currentQuestionSlug);
      let nextVisibleIdx = idx + 1;
      while (
        nextVisibleIdx < currentVisibleQuestions.length &&
        !currentVisibleQuestions[nextVisibleIdx].visible
      ) {
        nextVisibleIdx++;
      }
      if (nextVisibleIdx < currentVisibleQuestions.length) {
        setCurrentQuestionSlug(currentVisibleQuestions[nextVisibleIdx].slug);
      } else {
        if (currentView == "business-survey") {
          setCurrentView("consumer-collection-incentives-survey");
          setCurrentQuestionSlug(visibleIncentivesQuestions[0].slug);
        } else if (currentView === "consumer-collection-incentives-survey") {
          if (org?.featureGdpr) {
            setCurrentView("eea-uk-survey-welcome");
          } else if (org.featurePipeda) {
            setCurrentView("pipeda-privacy-officer");
          } else {
            setCurrentView("hr-survey");
            setCurrentQuestionSlug(visibleHrQuestions[0].slug);
          }
        } else if (currentView === "eea-uk-survey") {
          if (org.featurePipeda) {
            setCurrentView("pipeda-privacy-officer");
          } else {
            setCurrentView("hr-survey");
            setCurrentQuestionSlug(visibleHrQuestions[0].slug);
          }
        } else if (currentView === "hr-survey") {
          setCurrentView("finished-review-modal");
        }
      }
    },
    [
      bizAnswers,
      sellingAndSharingAnswersForBusinessSurvey,
      dataRecipientsForBusinessSurvey,
      dataMap,
      incentivesAnswers,
      eeaAnswers,
      eeaQuestionsWithVisibility,
      hrAnswers,
      currentView,
      org,
      privacyCenter,
      pcSettings,
      currentQuestionSlug,
      currentAnalyticsStep,
      allFlaggedForReviewSlugs,
      captureProductAnalyticsEvent,
    ],
  );

  if (!org || currentView === null) {
    return <PageLoading />;
  }

  let isShowingWelcomeMessage =
    currentView === "business-survey-welcome" || currentView === "eea-uk-survey-welcome";
  let isShowingSurvey =
    currentView === "business-survey" ||
    currentView === "consumer-collection-incentives-survey" ||
    currentView === "eea-uk-survey" ||
    currentView === "hr-survey";

  let isPrevVisible = currentView !== "business-survey-welcome";
  let isNextVisible = currentView !== "business-survey-welcome";
  let isNextEnabled = (() => {
    if (isShowingSurvey) {
      // For data source questions, enable the next button after a relevant vendor has been added
      if (currentQuestion && currentQuestion.type === "custom-add-data-source") {
        let filteredVendors = filterVendorsForDataSourceSurveyQuestion(currentQuestion);
        let filteredVendorIds = new Set(filteredVendors.map((v) => v.id));
        return (
          (currentQuestion?.sources || []).filter((s) => filteredVendorIds.has(s.vendorId)).length >
          0
        );
      }

      // For the DPO question, enable next button if answer is No or if answer is Yes and DPO details have been added
      if (currentQuestion.slug === HAS_DATA_PRIVACY_OFFICER_SLUG) {
        return (
          currentRemoteAnswers[currentQuestion.slug] === "false" ||
          (currentRemoteAnswers[currentQuestion.slug] === "true" && hasDpo)
        );
      }

      // For the Designated Rep question, enable next button if answer is No or if answer is Yes and EEA/UK contact details have been added
      if (currentQuestion.slug === HAS_DESIGNATED_REP_SLUG) {
        return (
          currentRemoteAnswers[currentQuestion.slug] === "false" ||
          (currentRemoteAnswers[currentQuestion.slug] === "true" && hasDesignatedRep)
        );
      }

      return (
        hasAnswered(currentRemoteAnswers, currentQuestion, /* debug = */ false) ||
        allFlaggedForReviewSlugs.includes(currentQuestionSlug)
      );
    }

    if (currentView === "pipeda-privacy-officer") {
      return pcSettings?.pipedaOfficerName?.length > 0;
    }

    return isShowingWelcomeMessage;
  })();

  return (
    <OmniBarProvider>
      <OmniBar></OmniBar>
      <style>{`
        @keyframes onboardingReveal {
          from { transform: translateY(15px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .onboarding-stagger {
          animation: onboardingReveal 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
          animation-delay: var(--reveal-delay, 0ms);
        }
      `}</style>
      <div className="tw-boundary">
        {currentView === "business-survey-welcome" && (
          <svg
            width="453"
            height="318"
            viewBox="0 0 453 318"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            style={{
              position: "fixed",
              bottom: 0,
              left: "97px",
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            <path d="M129 147.004L148.436 132.504" stroke="#5331C6" strokeLinecap="round" />
            <path d="M125.915 142.376L146.585 128.493" stroke="#5331C6" strokeLinecap="round" />
            <path
              d="M148.436 132.504L152.814 137.876C154.403 139.826 155.565 142.087 156.227 144.514L156.952 147.17C157.628 149.649 157.385 152.289 156.268 154.603C154.66 157.933 151.441 160.191 147.762 160.569L147.113 160.635C143.648 160.99 140.203 159.799 137.697 157.38L137.176 156.876C134.202 154.005 131.546 150.823 129.253 147.383L129 147.004"
              stroke="#5331C6"
              strokeLinecap="round"
            />
            <path
              d="M125.915 142.376L111.983 130.471C109.971 128.751 108.229 126.737 106.817 124.497L103.755 119.641C101.384 115.88 100.869 111.239 102.359 107.05L102.94 105.416C104.188 101.905 107.097 99.2409 110.705 98.3057C112.389 97.8689 114.152 97.8296 115.855 98.1907L118.773 98.8097C121.858 99.4642 124.784 100.723 127.38 102.514L128.562 103.329C131.102 105.081 133.368 107.201 135.283 109.621L139.091 114.431C142.414 118.629 144.953 123.393 146.585 128.493"
              stroke="#5331C6"
              strokeLinecap="round"
            />
            <path d="M41 49.5577L60.4362 35.0576" stroke="#5331C6" strokeLinecap="round" />
            <path d="M37.915 44.9299L58.5853 31.0469" stroke="#5331C6" strokeLinecap="round" />
            <path
              d="M60.4362 35.0576L64.8138 40.4301C66.4027 42.38 67.5654 44.6411 68.2272 47.0678L68.9516 49.7237C69.6277 52.2029 69.3848 54.8429 68.2676 57.157C66.6601 60.487 63.4405 62.7449 59.7622 63.1222L59.1131 63.1888C55.6483 63.5441 52.2031 62.3532 49.6974 59.9339L49.1756 59.43C46.2019 56.5589 43.5457 53.3763 41.2529 49.937L41 49.5577"
              stroke="#5331C6"
              strokeLinecap="round"
            />
            <path
              d="M37.915 44.9295L23.9833 33.0242C21.9705 31.3042 20.2287 29.2904 18.8168 27.0508L15.7553 22.1946C13.3842 18.4335 12.8695 13.7925 14.359 9.60322L14.9398 7.96966C16.1883 4.45825 19.0971 1.79466 22.7046 0.859382C24.3891 0.422646 26.1521 0.38328 27.8545 0.744388L30.7725 1.36336C33.8582 2.01791 36.7838 3.2769 39.3805 5.06773L40.5618 5.8824C43.1022 7.63444 45.3675 9.75517 47.283 12.1748L51.0907 16.9844C54.4144 21.1828 56.9533 25.9465 58.5853 31.0465"
              stroke="#5331C6"
              strokeLinecap="round"
            />
            <path d="M137.466 183.937L119.96 200.717" stroke="#5331C6" strokeLinecap="round" />
            <path d="M133.413 180.131L116.312 198.23" stroke="#5331C6" strokeLinecap="round" />
            <path
              d="M119.96 200.717L124.538 205.921C126.199 207.809 128.237 209.329 130.522 210.381L133.022 211.534C135.356 212.609 138 212.805 140.466 212.084C144.016 211.048 146.774 208.244 147.752 204.679L147.925 204.049C148.846 200.69 148.24 197.096 146.266 194.226L145.855 193.628C143.514 190.222 140.812 187.077 137.798 184.249L137.466 183.937"
              stroke="#5331C6"
              strokeLinecap="round"
            />
            <path
              d="M133.411 180.13L123.965 164.426C122.6 162.157 120.901 160.107 118.925 158.346L114.64 154.526C111.321 151.567 106.828 150.294 102.451 151.073L100.744 151.376C97.0748 152.029 93.9681 154.459 92.451 157.863C91.7426 159.452 91.4131 161.185 91.4887 162.923L91.6182 165.904C91.7551 169.055 92.5147 172.148 93.853 175.004L94.4618 176.304C95.7711 179.098 97.4894 181.682 99.5601 183.97L103.676 188.519C107.269 192.489 111.549 195.778 116.311 198.229"
              stroke="#5331C6"
              strokeLinecap="round"
            />
            <path d="M53.4658 88.937L35.9602 105.717" stroke="#5331C6" strokeLinecap="round" />
            <path d="M49.4128 85.1312L32.3125 103.23" stroke="#5331C6" strokeLinecap="round" />
            <path
              d="M35.9602 105.717L40.5376 110.921C42.199 112.809 44.2375 114.329 46.5218 115.381L49.0221 116.534C51.3558 117.609 53.9998 117.805 56.4664 117.084C60.0158 116.048 62.7736 113.244 63.752 109.679L63.9247 109.049C64.8463 105.69 64.2396 102.096 62.2664 99.2259L61.8554 98.6281C59.5138 95.2219 56.8125 92.0774 53.7982 89.249L53.4658 88.937"
              stroke="#5331C6"
              strokeLinecap="round"
            />
            <path
              d="M49.411 85.1297L39.9651 69.4261C38.6004 67.1574 36.9012 65.1075 34.925 63.3457L30.6399 59.5256C27.3211 56.5669 22.8284 55.2942 18.4509 56.0728L16.744 56.3764C13.0748 57.029 9.96814 59.459 8.45101 62.8629C7.74257 64.4525 7.41313 66.1848 7.48868 67.9234L7.61819 70.9036C7.75514 74.055 8.51466 77.148 9.85296 80.0044L10.4618 81.3038C11.7711 84.0983 13.4894 86.6822 15.5601 88.9704L19.6764 93.5187C23.2694 97.489 27.5494 100.778 32.3107 103.229"
              stroke="#5331C6"
              strokeLinecap="round"
            />
            <path
              d="M178.444 206.947C189.385 217.801 199.071 228.597 213.335 236.121C223.641 241.556 236.088 244.51 247.325 245.789C259.175 247.138 271.566 247.167 283.351 246.637C291.341 246.277 306.864 248.48 314.999 249.695C333.999 254.195 337.468 255.251 350.5 260.695C367.65 267.86 380.596 276.438 396.851 285.554C408.14 291.884 419.1 299.517 429.603 306.937C435.498 311.101 444.07 316.199 448.23 321.947"
              stroke="#5432D0"
              strokeLinecap="round"
              strokeDasharray="6 6"
            />
          </svg>
        )}
        <div className="min-h-screen flex" style={{ flexDirection: "column" }}>
          <div
            className={clsx(
              "top-0 sticky",
              "h-[64px] w-full",
              "bg-white shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)]",
              "px-[32px] py-[16px] flex items-center justify-between",
            )}
          >
            <div>
              <Link to={Routes.getCompliant.dashboard} aria-label="Go to dashboard">
                <img
                  src="/assets/images/truevault-logo.png"
                  className="w-[145px] h-[32px]"
                  alt="TrueVault Logo"
                />
              </Link>
            </div>
            <CurrentSurveyIndicator
              currentView={currentView}
              isGdprRequired={org?.featureGdpr}
              surveyProgress={surveyProgress}
            />
            <div>
              <OnboardingOrganizationDropdown
                orgName={org.name}
                userDisplayName={`${self.firstName} ${self.lastName}`}
              />
            </div>
          </div>
          <div
            className="flex-1 flex items-center mt-[120px]"
            style={{ flexDirection: "column", paddingBottom: "48px" }}
          >
            <div
              // key={currentView}
              key={`survey-card-${currentQuestionSlug ?? currentView}`}
              className={clsx(
                "rounded-[24px] bg-white border border-solid border-neutral-300",
                "p-[39px]",
                "grid place-items-center",
                "onboarding-stagger",
                isShowingWelcomeMessage && currentView === "business-survey-welcome"
                  ? "overflow-hidden"
                  : "overflow-y-auto shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)]",
              )}
              style={
                {
                  position: "relative",
                  zIndex: 1,
                  "--reveal-delay": "0ms",
                  width:
                    isShowingWelcomeMessage && currentView === "business-survey-welcome"
                      ? "896px"
                      : "890px",
                  ...(isShowingWelcomeMessage && currentView === "business-survey-welcome"
                    ? {
                        height: "500px",
                        boxShadow: "0px 2px 12px 0px rgba(0,0,0,0.25)",
                      }
                    : {}),
                } as React.CSSProperties
              }
            >
              {isShowingWelcomeMessage && (
                <WelcomeMessage
                  currentView={currentView}
                  isGdprRequired={org?.featureGdpr}
                  onNextClick={() => {
                    if (currentView == "business-survey-welcome") {
                      if (currentAnalyticsStep) {
                        captureProductAnalyticsEvent("onboarding_step_completed", {
                          ...currentAnalyticsStep,
                          completion_source: "start_button",
                        });
                      }
                      setCurrentView("business-survey");
                      let visibleBizQuestions = bizQuestionsWithVisibility.filter((q) => q.visible);
                      setCurrentQuestionSlug(visibleBizQuestions[0].slug);
                    } else if (currentView === "eea-uk-survey-welcome") {
                      setCurrentView("eea-uk-survey");
                      let visibleEeaQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);
                      setCurrentQuestionSlug(visibleEeaQuestions[0].slug);
                    }
                  }}
                />
              )}
              {isShowingSurvey &&
                (currentQuestion ? (
                  <div
                    className="onboarding-stagger"
                    style={{ "--reveal-delay": "40ms", width: "100%" } as React.CSSProperties}
                  >
                    <SurveyQuestion
                      key={currentQuestion.slug}
                      question={(() => {
                        let result = currentQuestion;
                        // For the DPO question, disable the "No" option if DPO is already set since we don't let users remove a DPO after it's been added (at least from the survey)
                        if (currentQuestion.slug === HAS_DATA_PRIVACY_OFFICER_SLUG && hasDpo) {
                          (result as SurveyBooleanQuestionProps).noDisabled = true;
                          (result as SurveyBooleanQuestionProps).noDisabledTooltip =
                            "You have already added a DPO.";
                          (result as SurveyBooleanQuestionProps).yesLabel = (
                            <span className="inline-flex items-center gap-[8px]">
                              <CheckIcon className="w-[20px] h-[20px]" />
                              DPO details were added
                            </span>
                          );
                        }
                        if (currentQuestion.slug === HAS_DESIGNATED_REP_SLUG && hasDesignatedRep) {
                          (result as SurveyBooleanQuestionProps).yesLabel = (
                            <span className="inline-flex items-center gap-[8px]">
                              <CheckIcon className="w-[20px] h-[20px]" />
                              Rep details were added
                            </span>
                          );
                        }
                        return result;
                      })()}
                      answer={
                        currentQuestion.visible ? currentAnswers[currentQuestion.slug] : undefined
                      }
                      onAnswer={async (ans) => {
                        let updatedAnswers = await currentSetAnswer(
                          currentQuestion.slug,
                          ans,
                          currentQuestionText,
                          /* flaggedForReview = */ allFlaggedForReviewSlugs.includes(
                            currentQuestion.slug,
                          ),
                        );

                        if (currentQuestion.slug === HAS_DESIGNATED_REP_SLUG) {
                          if (ans === "true") {
                            setIsDesignatedRepDialogOpen(true);
                          } else {
                            await Api.tasks.createEeaDesignatedRepresentativeTask(org.id);
                          }
                          // Return early without auto-advancing so the user can either see the dialog or the task created info message
                          return;
                        }

                        // If they answered "Yes" to the DPO question, open the DPO dialog (and if they answer "No", proceed to next question like normal)
                        if (
                          currentQuestion.slug === HAS_DATA_PRIVACY_OFFICER_SLUG &&
                          ans === "true"
                        ) {
                          setIsEditDpoDialogOpen(true);
                          return;
                        }

                        // for boolean questions, auto-advance to the next question after the user answers
                        if (currentQuestion.type === "boolean" && updatedAnswers) {
                          moveToNextQuestion(updatedAnswers, "answer_auto_advance");
                        }
                      }}
                      loading={
                        currentAnswers[currentQuestion.slug] !=
                        currentRemoteAnswers[currentQuestion.slug]
                      }
                      useNewDesign={true}
                    />
                    {currentQuestion.slug === HAS_DESIGNATED_REP_SLUG &&
                      currentAnswers[currentQuestion.slug] === "false" && (
                        <SurveyQuestionSuccessMessage messageText="We've added an item on your to-do list to help you set up a Designated Representative for your business in the EEA/UK." />
                      )}
                    <div
                      className="flex justify-end mt-[27px] onboarding-stagger"
                      style={{ "--reveal-delay": "120ms" } as React.CSSProperties}
                    >
                      <FlagForReviewButton
                        isFlagged={allFlaggedForReviewSlugs.includes(currentQuestion.slug)}
                        onClick={async () => {
                          let newIsFlaggedForReview = !allFlaggedForReviewSlugs.includes(
                            currentQuestion.slug,
                          );
                          await currentSetAnswer(
                            currentQuestion.slug,
                            currentAnswers[currentQuestion.slug],
                            currentQuestionText,
                            /* flaggedForReview = */ newIsFlaggedForReview,
                          );

                          if (newIsFlaggedForReview === true) {
                            if (currentAnalyticsStep) {
                              captureProductAnalyticsEvent("onboarding_step_skipped", {
                                ...currentAnalyticsStep,
                                skip_reason: "flagged_for_review",
                              });
                            }
                            showSnackbar("Marked for review.", { variant: "info" });
                          } else {
                            if (currentAnalyticsStep) {
                              captureProductAnalyticsEvent("onboarding_step_skip_reversed", {
                                ...currentAnalyticsStep,
                                skip_reason: "flagged_for_review",
                              });
                            }
                            showSnackbar("Unmarked for review.", { variant: "info" });
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div>No question for slug {currentQuestionSlug}</div>
                ))}
              {currentView === "pipeda-privacy-officer" && (
                <div key={currentView}>
                  <div
                    className="text-[30px] font-bold text-neutral-800 text-left onboarding-stagger"
                    style={{ "--reveal-delay": "40ms" } as React.CSSProperties}
                  >
                    Canadian Data Practices
                  </div>
                  <div
                    className="mt-[24px] onboarding-stagger"
                    style={{ "--reveal-delay": "120ms" } as React.CSSProperties}
                  >
                    <PrivacyCenterPipedaPrivacyOfficerRow
                      organization={pcSettings.organization}
                      vertical
                      useNewDesign
                      dpoName={pcSettings.dpoName}
                      dpoEmail={pcSettings.dpoEmail}
                      dpoPhone={pcSettings.dpoPhone}
                      dpoAddress={pcSettings.dpoAddress}
                      pipedaOfficerName={pcSettings.pipedaOfficerName}
                      pipedaOfficerEmail={pcSettings.pipedaOfficerEmail}
                      pipedaOfficerPhone={pcSettings.pipedaOfficerPhone}
                      pipedaOfficerAddress={pcSettings.pipedaOfficerAddress}
                      handleFormSubmit={pcSettings.handleFormSubmit}
                    />
                  </div>
                </div>
              )}
            </div>
            <div
              // key={currentQuestionSlug ?? currentView}
              key={`survey-nav-${currentQuestionSlug ?? currentView}`}
              className="w-[825px] h-[38px] mt-[28px] flex items-center justify-between onboarding-stagger"
              style={{ "--reveal-delay": "160ms" } as React.CSSProperties}
            >
              <div>
                {isPrevVisible && (
                  <Button
                    color="secondary"
                    onClick={() => {
                      moveToPrevQuestion();
                    }}
                  >
                    Previous Question
                  </Button>
                )}
              </div>
              <div>
                {isNextVisible && (
                  <Button
                    color={currentView === "eea-uk-survey-welcome" ? "primary" : "secondary"}
                    disabled={!isNextEnabled}
                    onClick={async () => {
                      // For data source questions, also set the answer to "true" (the added vendors are stored separately from the answer)
                      if (
                        isShowingSurvey &&
                        currentQuestion.type === "custom-add-data-source" &&
                        currentAnswers[currentQuestion.slug] !== "true"
                      ) {
                        let updatedAnswers = await currentSetAnswer(
                          currentQuestion.slug,
                          "true",
                          currentQuestionText,
                          /* flaggedForReview = */ allFlaggedForReviewSlugs.includes(
                            currentQuestion.slug,
                          ),
                        );

                        // It's fine to fallback to using the current answers (which won't reflect setting the current question to "true") since there is no survey question whose visibility is dependent on the answer to a data sources question
                        moveToNextQuestion(updatedAnswers || currentAnswers);
                      } else {
                        moveToNextQuestion(currentAnswers);
                      }
                    }}
                  >
                    Next Question
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {currentView === "finished-review-modal" && (
          <FinishedReviewModal
            orgId={org.id}
            onComplete={() => {
              if (currentAnalyticsStep) {
                captureProductAnalyticsEvent("onboarding_step_completed", {
                  ...currentAnalyticsStep,
                  completion_source: "review_answers_button",
                });
              }
              captureProductAnalyticsEvent("onboarding_flow_completed", {
                completion_mode: "completed",
              });
            }}
          />
        )}

        <EditDpoDialog
          open={isEditDpoDialogOpen}
          onClose={() => setIsEditDpoDialogOpen(false)}
          onDone={async (values: DpoValues) => {
            await pcSettings.handleFormSubmit({
              dpoOfficerName: values.name,
              dpoOfficerEmail: values.email,
              dpoOfficerPhone: values.phone,
              dpoOfficerAddress: values.address,
            });
          }}
          values={{
            name: pcSettings?.dpoName,
            email: pcSettings?.dpoEmail,
            phone: pcSettings?.dpoPhone,
            address: pcSettings?.dpoAddress,
          }}
        />

        <EditEeaContactDialog
          open={isDesignatedRepDialogOpen}
          onClose={() => setIsDesignatedRepDialogOpen(false)}
          onDone={(values: EeaContactValues) => {
            pcSettings.handleFormSubmit({
              eeaContactName: values.eeaContactName,
              eeaContactAddress: values.eeaContactAddress,
              eeaContactEmail: values.eeaContactEmail,
              eeaContactPhone: values.eeaContactPhone,
            });
          }}
          values={{
            eeaContactName: pcSettings.eeaContactName,
            eeaContactAddress: pcSettings?.eeaContactAddress,
            eeaContactEmail: pcSettings?.eeaContactEmail,
            eeaContactPhone: pcSettings?.eeaContactPhone,
          }}
        />
      </div>
    </OmniBarProvider>
  );
}

function WelcomeMessage({
  currentView,
  isGdprRequired,
  onNextClick,
}: {
  currentView: CurrentView;
  isGdprRequired: boolean;
  onNextClick: () => void;
}) {
  if (currentView === "business-survey-welcome") {
    let steps = [
      { title: "Business survey", description: "General data handling practices" },
      ...(isGdprRequired
        ? [{ title: "GDPR Survey", description: "Data handling in the EU/UK" }]
        : []),
      { title: "HR survey", description: "Employees and applicants data handling practices" },
    ];
    let stepCountWord = isGdprRequired ? "three" : "two";
    return (
      <div key={currentView} className="flex gap-[72px]" style={{ width: "100%", height: "422px" }}>
        <div className="flex-1" style={{ display: "flex", flexDirection: "column" }}>
          <div
            className="font-bold onboarding-stagger"
            style={
              {
                color: "#332E42",
                fontSize: "30px",
                lineHeight: "40px",
                "--reveal-delay": "40ms",
              } as React.CSSProperties
            }
          >
            Great work! Let's build your{" "}
            <span
              style={{
                display: "inline-block",
                backgroundColor: "#5331C6",
                color: "white",
                padding: "0 6px",
                lineHeight: "40px",
              }}
            >
              business data profile
            </span>{" "}
            with {stepCountWord} easy steps.
          </div>
          <div
            className="mt-[24px] onboarding-stagger"
            style={
              {
                color: "#534E65",
                fontSize: "20px",
                lineHeight: "28px",
                "--reveal-delay": "80ms",
              } as React.CSSProperties
            }
          >
            The data profile will be the foundation for your tailored compliance setup.
          </div>
          <div
            className="onboarding-stagger"
            style={
              {
                marginTop: "auto",
                paddingTop: "48px",
                "--reveal-delay": "160ms",
              } as React.CSSProperties
            }
          >
            <Button color="primary" onClick={onNextClick}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                Start
                <ArrowLongRightIcon style={{ width: "20px", height: "20px" }} />
              </span>
            </Button>{" "}
          </div>
        </div>
        <div
          className="flex-1 onboarding-stagger"
          style={
            {
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              "--reveal-delay": "120ms",
            } as React.CSSProperties
          }
        >
          {steps.map((step, index) => (
            <div key={step.title} style={{ display: "flex", gap: "24px" }}>
              <div
                className="font-medium"
                style={{
                  color: "#240D73",
                  fontSize: "20px",
                  lineHeight: "28px",
                  minWidth: "32px",
                }}
              >
                {String(index + 1).padStart(2, "0")}
              </div>
              <div className="flex-1">
                <div
                  className="font-semibold"
                  style={{ color: "#240D73", fontSize: "20px", lineHeight: "28px" }}
                >
                  {step.title}
                </div>
                <div
                  style={{
                    color: "#6B6B7E",
                    fontSize: "20px",
                    lineHeight: "28px",
                    marginTop: "4px",
                  }}
                >
                  {step.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  let imgSrcUrl = (() => {
    if (currentView === "eea-uk-survey-welcome")
      return "/assets/images/get_compliant/business_survey_gdpr_welcome.svg";
    return "";
  })();

  return (
    <div key={currentView} className="flex items-center" style={{ flexDirection: "column" }}>
      <div
        className="text-[30px] font-bold text-center text-netural-800 leading-[45px] onboarding-stagger"
        style={{ "--reveal-delay": "40ms" } as React.CSSProperties}
      >
        {currentView === "eea-uk-survey-welcome" && "Now let's review your GDPR data practices"}
      </div>
      <div
        className="mt-[36px] onboarding-stagger"
        style={{ "--reveal-delay": "120ms" } as React.CSSProperties}
      >
        <img src={imgSrcUrl} alt="Survey welcome" />
      </div>
    </div>
  );
}

function CurrentSurveyIndicator({
  currentView,
  isGdprRequired,
  surveyProgress,
}: {
  currentView: CurrentView;
  isGdprRequired: boolean;
  surveyProgress: { biz: number; eea: number; hr: number };
}) {
  // let isBusinessSurveyComplete = !(
  //   currentView === "business-survey-welcome" ||
  //   currentView === "business-survey" ||
  //   currentView === "consumer-collection-incentives-survey"
  // );
  // let isEeaUkSurveyComplete =
  //   !isGdprRequired ||
  //   (isBusinessSurveyComplete &&
  //     !(currentView === "eea-uk-survey-welcome" || currentView === "eea-uk-survey"));
  let isHrSurveyComplete = currentView === "finished-review-modal";

  let segments: { label: string; progress: number }[] = [
    { label: "Business Survey", progress: surveyProgress.biz },
    ...(isGdprRequired ? [{ label: "GDPR Survey", progress: surveyProgress.eea }] : []),
    { label: "HR Survey", progress: isHrSurveyComplete ? 1 : surveyProgress.hr },
  ];

  let firstIncompleteIndex = segments.findIndex((s) => s.progress < 1);

  let filledBackground =
    "radial-gradient(141.69% 125.19% at 90.11% 100%, #3406CE 0%, #8378FF 100%)";
  let emptyBackground = "#F3F0FE";

  return (
    <div className="flex gap-[12px]">
      {segments.map((segment, index) => {
        let state: "completed" | "active" | "disabled" =
          segment.progress >= 1
            ? "completed"
            : index === firstIncompleteIndex
            ? "active"
            : "disabled";
        let fillPct = Math.max(0, Math.min(1, segment.progress)) * 100;
        return (
          <div key={segment.label} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div
              className={
                state === "disabled"
                  ? "text-[14px] text-neutral-400 font-medium"
                  : "text-[14px] text-neutral-800 font-medium"
              }
              style={{ lineHeight: 1 }}
            >
              {segment.label}
            </div>
            <div
              className="w-[250px] h-[12px] rounded-[999px] overflow-hidden"
              style={{ background: emptyBackground, position: "relative" }}
            >
              <div
                style={{
                  width: `${fillPct}%`,
                  height: "100%",
                  background: filledBackground,
                  transition: "width 200ms ease-out",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FinishedReviewModal({ orgId, onComplete }: { orgId: string; onComplete: () => void }) {
  return (
    <Modal
      headerIcon={
        <img
          src="/assets/images/get_compliant/great_work.svg"
          alt="Great work!"
          className="h-[50px] w-auto"
        />
      }
      title={
        <>
          Review your answers and any flagged settings
          <br />
          before continuing.
        </>
      }
      alignText="center"
      firstButton="Review Your Answers"
      firstButtonColor="primary"
      onFirstButtonClick={() => completeBusinessSurveyAndNavigate(orgId, onComplete)}
      contentPadding="px-[24px] pt-5 pb-0"
      buttonPadding="px-[24px] pt-0 pb-[24px]"
      showOverlay
      closeOnOverlayClick={false}
      closeButton={false}
    >
      <div style={{ marginTop: "4px", marginBottom: "24px" }}>
        <img
          src="/assets/images/get_compliant/review_survey_answers_modal.svg"
          alt="Review survey answers"
          className="block w-[234px] h-[211px] mx-auto"
        />
      </div>
    </Modal>
  );
}

function isSurveyView(currentView: CurrentView): boolean {
  return [
    "business-survey",
    "consumer-collection-incentives-survey",
    "eea-uk-survey",
    "hr-survey",
  ].includes(currentView);
}

function analyticsStepIdForView(currentView: CurrentView): string {
  switch (currentView) {
    case "business-survey-welcome":
      return "business_survey_welcome";
    case "eea-uk-survey-welcome":
      return "eea_uk_survey_welcome";
    case "pipeda-privacy-officer":
      return "pipeda_privacy_officer";
    case "finished-review-modal":
      return "finished_review";
    default:
      return currentView;
  }
}

export function SurveyQuestionSuccessMessage({
  messageText,
}: {
  messageText: string | React.ReactNode;
}) {
  return (
    <div className="bg-green-100 mt-[12px] max-w-[250px] mx-auto rounded-[6px] px-[12px] py-[7px] flex items-center gap-[14px]">
      <div>
        <CheckCircleIcon className="w-[16px h-[16px] text-green-400" />
      </div>
      <div className="ml-[12px] text-green-800 font-medium text-[12px] leading-4">
        {messageText}
      </div>
    </div>
  );
}

export function initialViewAndQuestionSlug(
  org: OrganizationDto,
  {
    bizAnswers,
    sellingAndSharingAnswers,
    dataRecipients,
    incentivesAnswers,
    hrAnswers,
    eeaAnswers,
  }: {
    bizAnswers: SurveyAnswers;
    sellingAndSharingAnswers?: SurveyAnswers;
    dataRecipients?: OrganizationDataRecipientDto[];
    incentivesAnswers: SurveyAnswers;
    hrAnswers: SurveyAnswers;
    eeaAnswers: SurveyAnswers;
  },
  privacyCenter: PrivacyCenterDto,
  pcSettings: PrivacyCenterSettingsProps,
  allFlaggedForReviewSlugs: string[],
): [CurrentView, string] {
  const flags = {
    isGdprRequired: org?.featureGdpr,
    dpoRequired: org?.featureGdpr && isDataPrivacyOfficerRequired(eeaAnswers, privacyCenter),
    hasDpo: Boolean(pcSettings?.dpoName) && Boolean(pcSettings?.dpoEmail),
    hasDesignatedRep: hasDesignatedRepresentative(pcSettings),
  };

  let {
    bizQuestionsWithVisibility,
    incentivesQuestionsWithVisibility,
    hrQuestionsWithVisibility,
    eeaQuestionsWithVisibility,
  } = allSurveyQuestionsWithVisibility(org, privacyCenter, pcSettings, {
    bizAnswers,
    sellingAndSharingAnswers,
    dataRecipients,
    incentivesAnswers,
    hrAnswers,
    eeaAnswers,
  });

  let bizSurveyVisibleQuestions = bizQuestionsWithVisibility.filter((q) => q.visible);
  let incentivesSurveyVisibleQuestions = incentivesQuestionsWithVisibility.filter((q) => q.visible);
  let hrVisibileQuestions = hrQuestionsWithVisibility.filter((q) => q.visible);
  let eeaVisibileQuestions = eeaQuestionsWithVisibility.filter((q) => q.visible);

  let numBizSurveyAnswered = bizSurveyVisibleQuestions.filter(
    (q) =>
      hasAnswered(bizAnswers, q, /* debug = */ false) || allFlaggedForReviewSlugs.includes(q.slug),
  ).length;
  let numIncentivesSurveyAnswered = incentivesSurveyVisibleQuestions.filter(
    (q) =>
      hasAnswered(incentivesAnswers, q, /* debug = */ false) ||
      allFlaggedForReviewSlugs.includes(q.slug),
  ).length;
  let numHrAnswered = hrVisibileQuestions.filter(
    (q) =>
      hasAnswered(hrAnswers, q, /* debug = */ false) || allFlaggedForReviewSlugs.includes(q.slug),
  ).length;
  let numEeaAnswered = eeaVisibileQuestions.filter(
    (q) =>
      hasAnswered(eeaAnswers, q, /* debug = */ false) || allFlaggedForReviewSlugs.includes(q.slug),
  ).length;

  if (numBizSurveyAnswered == 0) {
    return ["business-survey-welcome", null];
  } else if (numBizSurveyAnswered < bizSurveyVisibleQuestions.length) {
    let firstUnansweredBizQuestionSlug = bizSurveyVisibleQuestions.find(
      (q) =>
        !hasAnswered(bizAnswers, q, /* debug = */ false) ||
        allFlaggedForReviewSlugs.includes(q.slug),
    )?.slug;
    return ["business-survey", firstUnansweredBizQuestionSlug];
  } else if (numIncentivesSurveyAnswered < incentivesSurveyVisibleQuestions.length) {
    let firstUnansweredIncentivesQuestionSlug = incentivesSurveyVisibleQuestions.find(
      (q) =>
        !hasAnswered(incentivesAnswers, q, /* debug = */ false) ||
        allFlaggedForReviewSlugs.includes(q.slug),
    )?.slug;
    return ["consumer-collection-incentives-survey", firstUnansweredIncentivesQuestionSlug];
  } else if (numEeaAnswered == 0 && org?.featureGdpr) {
    return ["eea-uk-survey-welcome", null];
  } else if (numEeaAnswered < eeaVisibileQuestions.length && flags.isGdprRequired) {
    let firstUnansweredEeaQuestionSlug = eeaVisibileQuestions.find(
      (q) =>
        // For the DPO question, it is considered unanswered if a DPO is required, and the user has selected "Yes" but hasn't actually entered DPO details
        (q.slug === HAS_DATA_PRIVACY_OFFICER_SLUG &&
          flags.dpoRequired &&
          eeaAnswers[q.slug] === "true" &&
          !flags.hasDpo) ||
        // For the Designated Rep question, it is considered unanswered if the user selected "Yes" but hasn't actually entered Designated Representative details
        (q.slug === HAS_DESIGNATED_REP_SLUG &&
          eeaAnswers[q.slug] === "true" &&
          !flags.hasDesignatedRep) ||
        !hasAnswered(eeaAnswers, q, /* debug = */ false) ||
        allFlaggedForReviewSlugs.includes(q.slug),
    )?.slug;
    return ["eea-uk-survey", firstUnansweredEeaQuestionSlug];
  } else if (org.featurePipeda && !(pcSettings.pipedaOfficerName?.length > 0)) {
    return ["pipeda-privacy-officer", null];
  } else if (numHrAnswered < hrVisibileQuestions.length) {
    let firstUnansweredHrQuestionSlug = hrVisibileQuestions.find(
      (q) =>
        !hasAnswered(hrAnswers, q, /* debug = */ false) ||
        allFlaggedForReviewSlugs.includes(q.slug),
    )?.slug;
    return ["hr-survey", firstUnansweredHrQuestionSlug];
  } else {
    return ["finished-review-modal", null];
  }
}

export function allSurveyQuestionsWithVisibility(
  org: OrganizationDto,
  privacyCenter: PrivacyCenterDto,
  pcSettings: PrivacyCenterSettingsProps,
  {
    bizAnswers,
    sellingAndSharingAnswers,
    dataRecipients,
    dataMap,
    incentivesAnswers,
    hrAnswers,
    eeaAnswers,
  }: {
    bizAnswers: SurveyAnswers;
    sellingAndSharingAnswers?: SurveyAnswers;
    dataRecipients?: OrganizationDataRecipientDto[];
    dataMap?: DataMapDto;
    incentivesAnswers: SurveyAnswers;
    hrAnswers: SurveyAnswers;
    eeaAnswers: SurveyAnswers;
  },
): {
  bizQuestionsWithVisibility: QuestionWithVisibility[];
  incentivesQuestionsWithVisibility: QuestionWithVisibility[];
  // sourcesQuestionsWithVisibility: QuestionWithVisibility[];
  hrQuestionsWithVisibility: QuestionWithVisibility[];
  eeaQuestionsWithVisibility: QuestionWithVisibility[];
} {
  let bizSurveyDefinition = businessSurvey(
    bizAnswers,
    sellingAndSharingAnswers,
    dataRecipients,
    dataMap,
  );
  let visibleBusinessQuestions = questionsWithVisibility(bizSurveyDefinition, bizAnswers);

  let incentivesSurveyDefinition = consumerCollectionIncentivesSurvey(incentivesAnswers, false);
  let incentivesQuestionsWithVisibility = questionsWithVisibility(
    incentivesSurveyDefinition,
    incentivesAnswers,
  ).filter((q) => q.slug === OFFERS_CONSUMER_INCENTIVES_SLUG);

  let hrDefinition = hrSurvey(hrAnswers, org?.featureGdpr);
  let hrQuestionsWithVisibility = questionsWithVisibility(hrDefinition, hrAnswers);

  let eeaDefinition = eeaUkSurvey(eeaAnswers, privacyCenter, pcSettings);
  let eeaQuestionsWithVisibility = questionsWithVisibility(eeaDefinition, eeaAnswers);

  return {
    bizQuestionsWithVisibility: visibleBusinessQuestions,
    incentivesQuestionsWithVisibility,
    hrQuestionsWithVisibility,
    eeaQuestionsWithVisibility,
  };
}
